import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ToastItem, toast } from "react-toastify";
import { InAppNotification } from "@/components/common/inAppNotifications/inAppNotification";
import {
  useCloseInAppNotificationMutation,
  useInAppNotificationsSubscription,
} from "@/graphql/queries/notifications/inAppNotifications.graphql.types";
import useErrorLogger from "@/utils/useErrorLogger";
import useFeatureFlags from "../common/useFeatureFlags";
import { useUserMedspaId } from "../common/useUserMedspa";

type ShowNotificationProps = {
  message: string;
  date: Date;
  id: string;
};

export const useInAppNotifications = () => {
  const { checkInNotificationsV1Enabled } = useFeatureFlags();
  const shownRef = useRef<Set<string>>(new Set());
  const userMedspaId = useUserMedspaId();

  useEffect(() => {
    if (!userMedspaId) {
      shownRef.current.clear();
      toast.dismiss();
    }
  }, [userMedspaId]);

  const logError = useErrorLogger();
  const { data } = useInAppNotificationsSubscription({
    skip: !userMedspaId || !checkInNotificationsV1Enabled,
  });
  const [temporaryClosedNotifications, setTemporaryClosedNotifications] =
    useState<Set<string>>(new Set());

  const [closeNotificationMutation] = useCloseInAppNotificationMutation();
  const closeNotification = useCallback(
    async (id: string) => {
      shownRef.current.delete(id);
      try {
        await closeNotificationMutation({
          variables: { id },
          optimisticResponse: {
            closeInAppNotification: {
              id,
            },
          },
          update(_, { data }) {
            // subscriptions works differently than query, cache.modify doesn't work with
            // data from subscription, so we need to update the temporary closed notifications state manually
            if (data.closeInAppNotification?.id === id) {
              setTemporaryClosedNotifications((prev) => {
                const newSet = new Set(prev);
                newSet.add(id);
                return newSet;
              });
            }
          },
        });
      } catch (error) {
        setTemporaryClosedNotifications((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        logError(error, "Error closing in-app notification");
      }
    },
    [closeNotificationMutation, setTemporaryClosedNotifications, logError]
  );

  const notificationsToShow = useMemo(() => {
    if (temporaryClosedNotifications.size === 0) {
      return data?.inAppNotification ?? [];
    }

    return (
      data?.inAppNotification.filter(
        (notification) => !temporaryClosedNotifications.has(notification.id)
      ) ?? []
    );
  }, [data, temporaryClosedNotifications]);

  useEffect(() => {
    for (const { id, message, created } of notificationsToShow) {
      if (shownRef.current.has(id)) {
        continue;
      }

      showNotification({
        message,
        date: new Date(created),
        id,
      });

      shownRef.current.add(id);
    }
  }, [notificationsToShow, closeNotification]);

  useEffect(() => {
    const unsubscribe = toast.onChange((payload: ToastItem) => {
      if (payload.status === "removed") {
        // We cannot change ToastItem type so we need to typecast data
        closeNotification((payload.data as { id: string }).id);
      }
    });
    return () => unsubscribe();
  }, [closeNotification]);
};

const showNotification = ({ message, date, id }: ShowNotificationProps) => {
  toast(
    (toast) => (
      <InAppNotification
        message={message}
        date={date}
        onClose={toast.closeToast}
      />
    ),
    {
      position: "top-right",
      closeButton: false,
      data: {
        id,
      },
    }
  );
};
